import React from "react";
import { Table, Typography, Alert } from "antd";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { AlignType } from "rc-table/lib/interface";
import GET_INVOICE_DETAILS from "graphql/queries/GetInvoiceDetails";
import { Breakpoint } from "antd/lib/_util/responsiveObserve";

// styling
import getCurrencySign from "utils/currency";
import outputManuallySetPrice from "utils/outputManuallySetPrice";
import { FormCard, CardTitle } from "./RegistrationStyle";

const { Text } = Typography;

const listOfDollarCurrency = ["usd", "eur", "sgd", "gbp", "aud", "cad"];

const outputPricetoString = (cents: number, currency: string) => {
  let dollarToString;
  if (listOfDollarCurrency.includes(currency)) {
    const centsInDollars = cents * 0.01;
    dollarToString = centsInDollars?.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else {
    dollarToString = outputManuallySetPrice(cents);
  }

  const currencySign = getCurrencySign(currency);

  return (
    <span>
      <span // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: currencySign,
        }}
      />
      {dollarToString}
    </span>
  );
};

type TProps = {
  stripeInvoiceId?: string;
};
const Confirmation = ({ stripeInvoiceId }: TProps) => {
  const { t } = useTranslation("registration");
  const { data, loading, error } = useQuery(GET_INVOICE_DETAILS, {
    variables: {
      stripeInvoiceId,
    },
  });

  if (loading) return <Alert type="info" message="Loading..." />;
  if (error) return <Alert type="warning" message="Cannot load invoice" />;

  const invoice = data.invoiceDetails;
  const { subtotal, discountAmount, total, currency } = invoice;
  const invoiceLineItems = invoice.lines;

  const columns = [
    {
      title: t("Subscription"),
      dataIndex: "description",
      responsive: ["sm"] as Breakpoint[],
    },
    {
      title: "",
    },
    {
      title: t("Amount"),
      dataIndex: "amount",
      align: "right" as AlignType,
      width: 100,
      render: (value: number) => outputPricetoString(value, currency),
    },
  ];

  return (
    <FormCard>
      <CardTitle>{t("INVOICE")}</CardTitle>
      <br />
      <Table
        columns={columns}
        dataSource={invoiceLineItems}
        pagination={false}
        summary={() => {
          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} />
                <Table.Summary.Cell index={1}>
                  <Text>{t("Subtotal")}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <Text>{outputPricetoString(subtotal, currency)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} />
                <Table.Summary.Cell index={1}>
                  <Text>{t("Discount")}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <Text>
                    {discountAmount > 0 ? "-" : ""}
                    {outputPricetoString(discountAmount, currency)}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} />
                <Table.Summary.Cell index={1}>
                  <Text strong>{t("Total")}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <Text strong>{outputPricetoString(total, currency)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          );
        }}
      />
    </FormCard>
  );
};

export default Confirmation;
